<script setup lang="ts">
/**
 * `DialogWithButtons.vue` — port of
 * `Content/Controls/DialogWithButtons.prefab`: the centered dialog card with a
 * header block and a two-button row.
 *
 * Prefab geometry kept:
 *  - root 300×240 default, centered; callers override via `width` / `height`
 *    (Unity binds `ConfirmationViewModel.WindowSize` onto `sizeDelta`, whose
 *    `ConfirmationArgs` default is 320×240).
 *  - `Panel` (header region): 30 px side insets, top 30 px, bottom 80 px
 *    (`SizeDelta = (-60, -110)`, `AnchoredPosition.y = 25`).
 *  - `ButtonsPanel`: 25 px side insets (`SizeDelta.x = -50`), 44 px tall,
 *    30 px off the bottom, `HorizontalLayoutGroup` spacing 10 with force-expand
 *    on both axes → two equal-width buttons.
 *  - Child order 0 = `CancelButton` (`ButtonGrey`), 1 = `ConfirmButton`
 *    (`ButtonRed`), i.e. cancel LEFT, confirm RIGHT.
 *  - Each button is bound through `StringEmptyToBoolAdapter`, so an EMPTY
 *    label hides that button — this is how Unity renders OK-only alerts
 *    (`new ConfirmationArgs(msg, "", "OK", "")`).
 *  - `TextHeader` is 25 px, left-aligned (`m_HorizontalAlignment: 1`), and the
 *    `ConfirmationView` instance overrides it to vertically middle
 *    (`m_VerticalAlignment: 512`).
 *
 * Deviations: the header's title/detail split is two elements rather than
 * Unity's single TMP rich-text string (see `IConfirmationModel.ConfirmationArgs`),
 * and the card clamps to the viewport so a small window can't clip it.
 */
import Button from "./Button.vue";

defineOptions({ name: "DialogWithButtons" });

withDefaults(
  defineProps<{
    headerText: string;
    detailText?: string;
    confirmText: string;
    cancelText: string;
    /** Unity `SizeDelta.x`; `ConfirmationArgs` defaults to 320. */
    width?: number;
    /** Unity `SizeDelta.y`; `ConfirmationArgs` defaults to 240. */
    height?: number;
  }>(),
  { width: 320, height: 240 },
);

const emit = defineEmits<{
  (e: "confirm"): void;
  (e: "cancel"): void;
}>();
</script>

<template>
  <div
    class="cf-dwb"
    role="alertdialog"
    aria-modal="true"
    :style="{ width: `${width}px`, minHeight: `${height}px` }"
  >
    <div class="cf-dwb__panel">
      <p class="cf-dwb__header">{{ headerText }}</p>
      <p v-if="detailText" class="cf-dwb__detail">{{ detailText }}</p>
      <slot />
    </div>

    <div class="cf-dwb__buttons">
      <!-- Empty label hides the button (Unity `StringEmptyToBoolAdapter`). -->
      <Button
        v-if="cancelText"
        class="cf-dwb__button"
        variant="grey"
        @click="emit('cancel')"
      >
        {{ cancelText }}
      </Button>
      <Button
        v-if="confirmText"
        class="cf-dwb__button"
        variant="red"
        @click="emit('confirm')"
      >
        {{ confirmText }}
      </Button>
    </div>
  </div>
</template>

<style scoped>
.cf-dwb {
  position: relative;
  display: flex;
  flex-direction: column;
  max-width: calc(100vw - 40px);
  max-height: calc(100vh - 40px);
  background: var(--cf-color-panel);
  border-top: none; /* glow via --cf-panel-shadow */
  border-radius: var(--cf-panel-radius);
  box-shadow: var(--cf-panel-shadow);
  backdrop-filter: var(--cf-panel-blur);
  -webkit-backdrop-filter: var(--cf-panel-blur);
  overflow: hidden;
}

/* Unity `Panel`: 30 side, 30 top, 80 bottom — the bottom inset is the
   ButtonsPanel's 44 + 30 reservation plus its own gap. */
.cf-dwb__panel {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: var(--cf-space-4);
  padding: 30px 30px 80px;
  overflow: auto;
}

/* `TextHeader.prefab` at its ConfirmationView override: 25 px, left, middle. */
.cf-dwb__header {
  margin: 0;
  font-size: 20px;
  line-height: 1.25;
  font-weight: var(--cf-font-weight-semibold);
  color: var(--cf-color-text);
  text-align: left;
  white-space: pre-line;
}

/* Unity's `<alpha=#9F><size=65%>` second header run. */
.cf-dwb__detail {
  margin: 0;
  font-size: 13px;
  line-height: 1.4;
  color: var(--cf-color-text);
  opacity: 0.62;
  text-align: left;
  white-space: pre-line;
}

.cf-dwb__buttons {
  position: absolute;
  left: 25px;
  right: 25px;
  bottom: 30px;
  display: flex;
  gap: 10px;
}

.cf-dwb__button {
  flex: 1 1 0;
  height: 44px;
}
</style>
